import React from 'react';
import {
  IntroductionContent,
  TextArea,
  CallsImgContent,
  CallsImg,
  ReadMoreButton,
  CallsFeatures,
  CallsAudioImgContainer,
  CallsAudioPlayerImg,
  FeaturesContainer,
  IllustrationContainer,
  FeatureText,
  CheckIcon,
  Title,
  Container,
  Divider,
  ModalButton,
  DataContainer,
} from './styles';
import { BdsButton, BdsTypo, BdsIllustration, BdsGrid, BdsIcon, BdsPaper, BdsAccordion, BdsAccordionHeader, BdsAccordionBody } from 'blip-ds/dist/blip-ds-react';
import { useContentLocalizer } from '../../../hooks/useContentLocalizer';
import { localization } from './localization';
import CallsHumanImg from '../../../assets/images/calls-promo.png';
import CallsAudioImg from '../../../assets/images/calls-audio.png';
import { ORGANIZATION_CALLS_OPEN_TERMS_MODAL, ORGANIZATION_CALLS_OPEN_READ_MORE } from '../../../shared/SegmentEvents';
import { createTrack } from '../../../api/SegmentApi';
import { useTenant } from '../../../contexts/TenantContext';
import { showTenantModal } from '../../../api/CommonApi';

interface CallsAdProps {
  onSuccess: () => void;
}

const CallsAd: React.FC<CallsAdProps> = ({ onSuccess }) => {
  const translate = useContentLocalizer(localization);
  const whatsAppBusiness = 'https://www.blip.ai/blog/whatsapp/whatsapp-business/';
  const { tenant: { info } } = useTenant();

  const showCallsModal = async () => {
    createTrack(info, ORGANIZATION_CALLS_OPEN_TERMS_MODAL);
    const modal = await showTenantModal('CallsTermsModal');
    if (modal.response === true) {
      onSuccess();
    }
  };

  const onReadMoreClick = () => {
    createTrack(info, ORGANIZATION_CALLS_OPEN_READ_MORE);
    window.open(translate.readMoreLink, '_blank');
  };

  return (
    <>
      <Container className="pb4">
        <IntroductionContent>
          <TextArea>
            <BdsTypo variant="fs-40" tag="h1" bold="semi-bold">
              {translate.title}
            </BdsTypo>
            <BdsTypo variant="fs-16" tag="p" bold="regular">
              {translate.formatString(translate.text, <u><a target="_blank" rel="noreferrer" href={whatsAppBusiness}>WhatsApp Business</a></u>)}
            </BdsTypo>
            <ModalButton>
              <BdsButton variant="solid" color="primary" onClick={() => showCallsModal()}>
                {translate.buttonText}
              </BdsButton>
              <div className="w-60" >
                <BdsTypo variant="fs-12" italic={true} >
                  {translate.secondaryText}
                </BdsTypo>
              </div>
            </ModalButton>
          </TextArea>
          <CallsImgContent>
            <CallsAudioImgContainer>
              <CallsAudioPlayerImg src={CallsAudioImg}></CallsAudioPlayerImg>
            </CallsAudioImgContainer>
            <CallsImg src={CallsHumanImg} />
          </CallsImgContent>
        </IntroductionContent>
        <FeaturesContainer>
          <CallsFeatures>
            <BdsGrid direction="row" alignItems="center" gap="6">
              <BdsGrid direction="column" alignItems="center" gap="2">
                <IllustrationContainer>
                  <BdsIllustration type="default" name="attendance-1"></BdsIllustration>
                </IllustrationContainer>
              </BdsGrid>
              <BdsGrid direction="row" alignItems="center" gap="2">
                <BdsGrid direction="column" alignItems="flex-start" gap="2">
                  <FeatureText variant="fs-32" >
                    {translate.features.title}
                  </FeatureText>
                  <BdsGrid gap="1">
                    <CheckIcon name="checkball" theme="solid" />
                    <FeatureText variant="fs-16" >
                      {translate.formatString(translate.features.incomingCall, <b>{translate.features.incomingCallBold}</b>)}
                    </FeatureText>
                  </BdsGrid>
                  <BdsGrid gap="1">
                    <CheckIcon name="checkball" theme="solid" />
                    <FeatureText variant="fs-16" >
                      {translate.formatString(translate.features.outboundCall, <b>{translate.features.outboundCallBold}</b>)}
                    </FeatureText>
                  </BdsGrid>
                  <BdsGrid gap="1">
                    <CheckIcon name="checkball" theme="solid" />
                    <FeatureText variant="fs-16" >
                      {translate.formatString(translate.features.recording, <b>{translate.features.recordigingBold}</b>)}
                    </FeatureText>
                  </BdsGrid>
                  <ReadMoreButton> <BdsButton className="mt3" variant="outline" color="content" onClick={onReadMoreClick}>{translate.features.readMore}</BdsButton></ReadMoreButton>
                </BdsGrid>
                <BdsGrid direction="column" alignItems="flex-start" gap="2">
                  <BdsGrid gap="1">
                    <CheckIcon name="checkball" theme="solid" />
                    <FeatureText variant="fs-16" >
                      {translate.formatString(translate.features.transcription, <b>{translate.features.transcriptionBold}</b>)}
                    </FeatureText>
                  </BdsGrid>
                  <BdsGrid gap="1">
                    <CheckIcon name="checkball" theme="solid" />
                    <FeatureText variant="fs-16" >
                      {translate.formatString(translate.features.history, <b>{translate.features.historyBold}</b>)}
                    </FeatureText>
                  </BdsGrid>
                  <BdsGrid gap="1">
                    <CheckIcon name="checkball" theme="solid" />
                    <FeatureText variant="fs-16" >
                      {translate.formatString(translate.features.management, <b>{translate.features.managementBold}</b>)}
                    </FeatureText>
                  </BdsGrid>
                </BdsGrid>
              </BdsGrid>
            </BdsGrid>
          </CallsFeatures>
        </FeaturesContainer>
        <Title className="ph4 pb4">
          <BdsGrid direction="row" gap="2" justifyContent="space-between" >
            <BdsGrid direction="column" gap="2" justifyContent="center" >
              <BdsTypo variant="fs-20" bold="regular">
                {translate.shortTitle}
              </BdsTypo>
              <BdsTypo variant="fs-32" bold="bold" className="break-spaces">
                {translate.shortDescription}
              </BdsTypo>
            </BdsGrid>
            <DataContainer className="pa4">
              <BdsGrid direction="column" gap="1"  >
                <BdsTypo variant="fs-32" bold="bold">
                  87%
                </BdsTypo>
                <BdsTypo variant="fs-16" bold="regular">
                  {translate.dataText}
                </BdsTypo>
              </BdsGrid>
            </DataContainer>
          </BdsGrid>
        </Title>
        <Title>
          <BdsTypo variant="fs-32" bold="bold">
            {translate.configurations.title}
          </BdsTypo>
        </Title>
        <BdsGrid direction="row" gap="2" justifyContent="space-between" >
          <BdsPaper className="pa4 w-100">
            <BdsGrid direction="column" gap="2">
              <BdsIcon name="checkball" className="color-primary" theme="outline" size="x-large" />
              <BdsTypo variant="fs-16" bold="bold">
                {translate.configurations.step1.title}
              </BdsTypo>
              <BdsTypo variant="fs-14">
                {translate.configurations.step1.description}
              </BdsTypo>
            </BdsGrid>
          </BdsPaper>
          <BdsPaper className="pa4 w-100">
            <BdsGrid direction="column" gap="2">
              <BdsIcon name="business" className="color-primary" theme="outline" size="x-large" />
              <BdsTypo variant="fs-16" bold="bold">
                {translate.configurations.step2.title}
              </BdsTypo>
              <BdsTypo variant="fs-14">
                {translate.configurations.step2.description}
              </BdsTypo>
            </BdsGrid>
          </BdsPaper>
          <BdsPaper className="pa4 w-100">
            <BdsGrid direction="column" gap="2">
              <BdsIcon name="builder-text-message" className="color-primary" theme="outline" size="x-large" />
              <BdsTypo variant="fs-16" bold="bold">
                {translate.configurations.step3.title}
              </BdsTypo>
              <BdsTypo variant="fs-14">
                {translate.configurations.step3.description}
              </BdsTypo>
            </BdsGrid>
          </BdsPaper>
        </BdsGrid>
        <BdsPaper className="pa4 w-100 mt3">
          <BdsGrid direction="row" gap="2" alignItems="center">
            <BdsGrid direction="column">
              <BdsIcon name="agent" className="color-primary" theme="outline" size="xx-large" />
            </BdsGrid>
            <BdsGrid direction="column" gap="2" >
              <BdsTypo variant="fs-16" bold="bold">
                {translate.configurations.step4.title}
              </BdsTypo>
              <BdsTypo variant="fs-14">
                {translate.configurations.step4.description}
              </BdsTypo>
            </BdsGrid>
          </BdsGrid>
        </BdsPaper>
        <Divider />
        <Title>
          <BdsTypo variant="fs-32" bold="bold">
            {translate.questions.title}
          </BdsTypo>
        </Title>
        <BdsAccordion>
          <BdsAccordionHeader accordionTitle={translate.questions.question1.title} />
          <BdsAccordionBody>
            <BdsTypo variant="fs-16">
              {translate.questions.question1.answer}
            </BdsTypo>
          </BdsAccordionBody>
        </BdsAccordion>
        <BdsAccordion>
          <BdsAccordionHeader accordionTitle={translate.questions.question2.title} />
          <BdsAccordionBody>
            <BdsTypo variant="fs-16">
              {translate.questions.question2.answer1}
            </BdsTypo>
            <BdsTypo variant="fs-16" >
              <li className="ml3"> {translate.formatString(translate.questions.question2.answer2, <b>{translate.questions.question2.answer2Bold}</b>)}</li>
            </BdsTypo>
            <BdsTypo variant="fs-16" >
              <li className="ml3"> {translate.formatString(translate.questions.question2.answer3, <b>{translate.questions.question2.answer3Bold}</b>)}</li>
            </BdsTypo>
          </BdsAccordionBody>
        </BdsAccordion>
        <BdsAccordion>
          <BdsAccordionHeader accordionTitle={translate.questions.question3.title} />
          <BdsAccordionBody>
            <BdsTypo variant="fs-16">
              {translate.questions.question3.answer}
            </BdsTypo>
          </BdsAccordionBody>
        </BdsAccordion>
        <BdsAccordion>
          <BdsAccordionHeader accordionTitle={translate.questions.question4.title} />
          <BdsAccordionBody>
            <BdsTypo variant="fs-16">
              {translate.questions.question4.answer}
            </BdsTypo>
          </BdsAccordionBody>
        </BdsAccordion>
        <BdsAccordion>
          <BdsAccordionHeader accordionTitle={translate.questions.question5.title} />
          <BdsAccordionBody>
            <BdsTypo variant="fs-16">
              {translate.questions.question5.answer}
            </BdsTypo>
          </BdsAccordionBody>
        </BdsAccordion>
        <BdsAccordion >
          <BdsAccordionHeader accordionTitle={translate.questions.question6.title} />
          <BdsAccordionBody>
            <BdsTypo variant="fs-16">
              {translate.questions.question6.answer}
            </BdsTypo>
          </BdsAccordionBody>
        </BdsAccordion>
      </Container>
    </>
  );
};

export default CallsAd;